import { useMemo, useState } from 'react';
import Card from '../ui/Card';
import { useAuth } from '../../contexts/AuthContext';
import { usePaymentMethods } from '../../hooks/usePaymentMethods';
import {
  deletePaymentMethod,
  setPaymentMethodEnabled,
  upsertPaymentMethod,
} from '../../utils/settingsMutations';
import PaymentMethodForm, { PAYMENT_METHOD_TYPES } from './PaymentMethodForm';

const typeLabel = (type) =>
  PAYMENT_METHOD_TYPES.find((item) => item.value === type)?.label || type;

const describeDetails = (method) => {
  if (!method) return '';
  if (method.type === 'bank_account') {
    const { bankName, accountName, accountNumber } = method.details || {};
    return [bankName, accountName, accountNumber].filter(Boolean).join(' · ');
  }
  if (method.details?.checkoutUrl) return method.details.checkoutUrl;
  return '';
};

const PaymentMethodsSettings = () => {
  const { currentUser } = useAuth();
  const { list, loading } = usePaymentMethods();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);

  const flash = (type, text) => {
    setMessage({ type, text });
    window.setTimeout(() => setMessage(null), 4000);
  };

  const grouped = useMemo(() => {
    const student = list.filter((m) => (m.availableFor || []).includes('student'));
    const reception = list.filter((m) =>
      (m.availableFor || []).includes('reception'),
    );
    return { student, reception };
  }, [list]);

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      await upsertPaymentMethod({
        methodId: editing?.id || null,
        values: {
          ...values,
          order: editing?.order ?? list.length,
        },
        operator: currentUser,
      });
      flash('success', editing ? 'Payment method updated.' : 'Payment method added.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (method) => {
    if (!window.confirm(`Delete "${method.label}"?`)) return;
    try {
      await deletePaymentMethod({ methodId: method.id });
      flash('success', 'Payment method removed.');
    } catch (err) {
      flash('error', err?.message || 'Failed to delete method.');
    }
  };

  const handleToggle = async (method) => {
    try {
      await setPaymentMethodEnabled({
        methodId: method.id,
        enabled: !method.enabled,
        operator: currentUser,
      });
      flash('success', method.enabled ? 'Method disabled.' : 'Method enabled.');
    } catch (err) {
      flash('error', err?.message || 'Failed to update method.');
    }
  };

  const handleReorder = async (method, direction) => {
    const peers = [...list].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const index = peers.findIndex((m) => m.id === method.id);
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= peers.length) return;
    const target = peers[targetIndex];
    try {
      await Promise.all([
        upsertPaymentMethod({
          methodId: method.id,
          values: { ...method, order: target.order ?? targetIndex },
          operator: currentUser,
        }),
        upsertPaymentMethod({
          methodId: target.id,
          values: { ...target, order: method.order ?? index },
          operator: currentUser,
        }),
      ]);
    } catch (err) {
      flash('error', err?.message || 'Failed to reorder.');
    }
  };

  const renderMethodCard = (method) => (
    <div
      key={method.id}
      className="flex flex-col gap-3 rounded-lg border border-stone-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h4 className="truncate text-base font-semibold text-stone-900">
            {method.label}
          </h4>
          <span className="inline-flex items-center rounded-full bg-stone-100 px-2 py-0.5 text-xs font-medium text-stone-700">
            {typeLabel(method.type)}
          </span>
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
              method.enabled
                ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
                : 'bg-stone-100 text-stone-600 ring-1 ring-stone-200'
            }`}
          >
            {method.enabled ? 'Enabled' : 'Disabled'}
          </span>
        </div>
        {describeDetails(method) ? (
          <p className="mt-1 truncate text-xs text-stone-500">
            {describeDetails(method)}
          </p>
        ) : null}
        {method.instructions ? (
          <p className="mt-1 text-xs text-stone-500">{method.instructions}</p>
        ) : null}
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => handleReorder(method, 'up')}
          className="rounded-md border border-stone-300 bg-white px-2 py-1 text-xs font-medium text-stone-600 hover:bg-stone-50"
          aria-label="Move up"
        >
          ↑
        </button>
        <button
          type="button"
          onClick={() => handleReorder(method, 'down')}
          className="rounded-md border border-stone-300 bg-white px-2 py-1 text-xs font-medium text-stone-600 hover:bg-stone-50"
          aria-label="Move down"
        >
          ↓
        </button>
        <button
          type="button"
          onClick={() => handleToggle(method)}
          className="rounded-md border border-stone-300 bg-white px-3 py-1.5 text-xs font-medium text-stone-700 hover:bg-stone-50"
        >
          {method.enabled ? 'Disable' : 'Enable'}
        </button>
        <button
          type="button"
          onClick={() => {
            setEditing(method);
            setFormOpen(true);
          }}
          className="rounded-md border border-stone-300 bg-white px-3 py-1.5 text-xs font-medium text-stone-700 hover:bg-stone-50"
        >
          Edit
        </button>
        <button
          type="button"
          onClick={() => handleDelete(method)}
          className="rounded-md border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
        >
          Delete
        </button>
      </div>
    </div>
  );

  return (
    <Card className="shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-stone-900">Payment methods</h2>
          <p className="text-sm text-stone-500">
            Control which options are available in student and reception payment
            flows.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
          className="inline-flex items-center gap-2 rounded-md bg-pink-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-pink-700"
        >
          Add payment method
        </button>
      </div>

      {message ? (
        <div
          className={`mt-4 rounded-md px-3 py-2 text-sm ${
            message.type === 'success'
              ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
              : 'bg-red-50 text-red-700 ring-1 ring-red-200'
          }`}
        >
          {message.text}
        </div>
      ) : null}

      <div className="mt-5 space-y-6">
        <section>
          <h3 className="text-sm font-semibold uppercase tracking-wide text-stone-500">
            Student facing ({grouped.student.length})
          </h3>
          <div className="mt-2 space-y-3">
            {loading ? (
              <p className="text-sm text-stone-500">Loading methods...</p>
            ) : grouped.student.length === 0 ? (
              <p className="text-sm text-stone-500">
                No student payment methods configured yet.
              </p>
            ) : (
              grouped.student.map(renderMethodCard)
            )}
          </div>
        </section>

        <section>
          <h3 className="text-sm font-semibold uppercase tracking-wide text-stone-500">
            Reception / staff ({grouped.reception.length})
          </h3>
          <div className="mt-2 space-y-3">
            {loading ? (
              <p className="text-sm text-stone-500">Loading methods...</p>
            ) : grouped.reception.length === 0 ? (
              <p className="text-sm text-stone-500">
                No reception methods configured yet.
              </p>
            ) : (
              grouped.reception.map(renderMethodCard)
            )}
          </div>
        </section>
      </div>

      <PaymentMethodForm
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={handleSubmit}
        method={editing}
        submitting={submitting}
      />
    </Card>
  );
};

export default PaymentMethodsSettings;
